import Tippy from '@tippyjs/react/headless';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { MdKeyboardArrowUp } from 'react-icons/md';
import Input from '../Input';
import LabeledContainer from '../LabeledContainer';
import css from './ColorField.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT_COLOR = '#ffffff';
function isValidHex(value) {
  return typeof value === 'string' && /^#(?:[0-9a-f]{3}){1,2}$/i.test(value);
}
function useToggle(initialValue = false) {
  const [value, setValue] = useState(initialValue);
  const toggle = () => setValue(prevValue => !prevValue);
  return [value, toggle];
}
function getContrastingTextColor(hex) {
  if (!isValidHex(hex)) return 'inherit';
  let normalized = hex.slice(1);
  if (normalized.length === 3) {
    normalized = normalized.split('').map(char => char + char).join('');
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  // https://stackoverflow.com/questions/596216/formula-to-determine-perceived-brightness-of-rgb-color
  // used Luminance (perceived option 1)
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 150 ? '#000000' : '#ffffff';
}
export default function ColorField({
  label,
  value,
  error,
  onChange
}) {
  const [isOpen, toggleOpen] = useToggle();
  const isFilled = isValidHex(value);
  const pickerColor = isFilled ? value : DEFAULT_COLOR;
  const textColor = getContrastingTextColor(value);
  const picker = /*#__PURE__*/_jsx("div", {
    className: css.colorPickerPopover,
    children: /*#__PURE__*/_jsx(HexColorPicker, {
      color: pickerColor,
      onChange: next => onChange?.(next)
    })
  });
  const handleInputFocus = () => {
    if (!isOpen) toggleOpen();
  };
  return /*#__PURE__*/_jsx(LabeledContainer, {
    label: label,
    children: /*#__PURE__*/_jsx(Tippy, {
      visible: isOpen,
      onClickOutside: toggleOpen,
      interactive: true,
      placement: "top-start",
      appendTo: () => document.body,
      render: attrs => /*#__PURE__*/_jsx("div", {
        ...attrs,
        children: picker
      }),
      children: /*#__PURE__*/_jsx("div", {
        className: css.colorFieldTrigger,
        children: /*#__PURE__*/_jsx(Input, {
          size: "small",
          value: value != null ? value : '',
          placeholder: "Select color",
          error: error,
          style: {
            backgroundColor: isFilled ? value : undefined,
            color: isFilled ? textColor : undefined,
            boxShadow: isFilled ? 'none' : undefined
          },
          onChange: e => onChange?.(e.target.value),
          onFocus: handleInputFocus,
          iconPosition: "right",
          icon: /*#__PURE__*/_jsx("button", {
            type: "button",
            className: css.colorChevron,
            style: {
              color: isFilled ? textColor : undefined
            },
            onClick: toggleOpen,
            "aria-label": `Pick color for ${label}`,
            children: /*#__PURE__*/_jsx(MdKeyboardArrowUp, {
              size: 18
            })
          })
        })
      })
    })
  });
}
ColorField.propTypes = {
  /** Label displayed above the field */
  label: PropTypes.string.isRequired,
  /** Current hex color value (e.g. #ffffff) */
  value: PropTypes.string,
  /** Validation error message */
  error: PropTypes.string,
  /** Called with the new hex string when the value changes */
  onChange: PropTypes.func
};